import type { ReactNode } from 'react';
import { insights } from '@repletoryreach/sdk-api';
import { Button, Card, Group, Stack, Text, Title } from '@repletoryreach/sdk-ui';
import { StatusBadge, type StatusTone } from '@repletoryreach/sdk-ui/data-display';
import type { Translate } from '../../i18n/I18nProvider';
import { formatLocalDate } from '../bookings/lib/time';

export const MUTED = 'var(--rr-color-text-muted)';

export const channelLabel = (t: Translate, c: string) => t(`insights.channel.${c}`, c);
export const depthLabel = (t: Translate, d: insights.InsightDepth) => t(`insights.depth.${d}.name`);
export const outcomeLabel = (t: Translate, o: string) => t(`insights.outcome.${o}`, o);
export const sentimentLabel = (t: Translate, s: string) => t(`insights.sentiment.${s}`, s);

/** The console's language as Intl wants it: South African, so dates read "1 September 2026" and decimals take a comma. */
export const intlTag = (locale: string) => (locale.includes('-') ? locale : `${locale}-ZA`);

/** "1 to 30 September 2026", in the reader's language. */
export function periodLabel(t: Translate, from: string, to: string, locale: string): string {
  const sameYear = from.slice(0, 4) === to.slice(0, 4);
  const tag = intlTag(locale);
  return t('insights.period', {
    from: formatLocalDate(from, tag, sameYear ? { day: 'numeric', month: 'long' } : { day: 'numeric', month: 'long', year: 'numeric' }),
    to: formatLocalDate(to, tag, { day: 'numeric', month: 'long', year: 'numeric' })
  });
}

const STATUS_TONE: Record<insights.InsightStatus, StatusTone> = {
  queued: 'pending', running: 'pending', done: 'healthy', failed: 'down', cancelled: 'inactive', skipped: 'warning'
};

export function ReportStatus({ status, t }: { status: insights.InsightStatus; t: Translate }) {
  return <StatusBadge status={STATUS_TONE[status]} size="sm">{t(`insights.status.${status}`)}</StatusBadge>;
}

/** "See 12 chats": the chats an item is based on, shown on the Chats tab. Nothing when it names none. */
export function SeeChats({ refs, onSee, t }: { refs: string[]; onSee?: (refs: string[]) => void; t: Translate }) {
  if (!onSee || refs.length === 0) return null;
  return (
    <Button size="xs" variant="subtle" onClick={() => onSee(refs)} data-testid="insights-see-chats">
      {refs.length === 1 ? t('insights.seeChats.one') : t('insights.seeChats.other', { count: refs.length })}
    </Button>
  );
}

/** A card with a title, an optional note under it, and its content. */
export function Panel({ title, note, actions, children, guide, testId }: {
  title: ReactNode; note?: ReactNode; actions?: ReactNode; children: ReactNode; guide?: string; testId?: string;
}) {
  return (
    <Card withBorder data-guide={guide} data-testid={testId}>
      <Stack gap="sm">
        <Group justify="space-between" align="flex-start" gap="sm" wrap="wrap">
          <Stack gap={2} style={{ minWidth: 0, flex: '1 1 240px' }}>
            <Title order={4}>{title}</Title>
            {note && <Text role="caption" c={MUTED}>{note}</Text>}
          </Stack>
          {actions}
        </Group>
        {children}
      </Stack>
    </Card>
  );
}

/** Text the AI wrote for the whole period: it does not change with the filters. */
export function WholeReport({ t }: { t: Translate }) {
  return <Text role="caption" c={MUTED} data-testid="insights-whole-report">{t('insights.report.whole')}</Text>;
}

export function Bullets({ items }: { items: ReactNode[] }) {
  if (items.length === 0) return null;
  return (
    <ul className="rr-insights__bullets">
      {items.map((x, i) => <li key={i}><Text role="bodySmall">{x}</Text></li>)}
    </ul>
  );
}

export function Muted({ children }: { children: ReactNode }) {
  return <Text role="bodySmall" c={MUTED}>{children}</Text>;
}

export const pct = (part: number, whole: number) => (whole > 0 ? Math.round((part / whole) * 100) : 0);
